// Help on every phone screen (18 §18.1, §18.6): the Help menu in the header, the screen's
// walkthrough with the steps the person's permissions allow, the first-visit nudge, and
// `recordWalkthrough` (queued offline like every command). A screen opened with `?tour=1` (or
// `?tour=demo`) starts its walkthrough by itself: that is how My training opens a screen.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  canSeeWalkthrough,
  type Permission,
  type TrainingRecords,
  type Walkthrough,
} from '@ecobox/domain';
import { permissionsFromClaims, sendWalkthroughRecord, watchMyTraining } from '@ecobox/firebase';
import { HelpMenu, usePageHelp, type PageHelp } from '@ecobox/ui';
import { useServices, useSession } from '../services';
import {
  ALL_OPS_WALKTHROUGHS,
  opsTraining,
  walkthroughForPath,
  type OpsTrainingEntry,
} from './index';

export const TOUR_PARAM = 'tour';

const ContentContext = createContext<readonly Walkthrough[]>(ALL_OPS_WALKTHROUGHS);

/** Replaces the walkthroughs (screen tests bring their own). */
export function HelpContentProvider({
  walkthroughs,
  children,
}: {
  walkthroughs: readonly Walkthrough[];
  children: ReactNode;
}) {
  return <ContentContext.Provider value={walkthroughs}>{children}</ContentContext.Provider>;
}

function useHeld(): ReadonlySet<Permission> {
  const { claims } = useSession();
  return useMemo(() => permissionsFromClaims(claims), [claims]);
}

/** The signed-in person's training records; undefined while they load. */
function useMyTrainingRecords(): TrainingRecords | undefined {
  const { client } = useServices();
  const { uid } = useSession();
  const [state, setState] = useState<{ uid: string; records: TrainingRecords } | null>(null);
  useEffect(() => {
    if (!uid) return;
    return watchMyTraining(
      client.db,
      uid,
      (records) => setState({ uid, records }),
      (err) => console.warn('Training records could not be read.', err.message),
    );
  }, [client.db, uid]);
  return state && state.uid === uid ? state.records : undefined;
}

interface OpsHelp {
  page: PageHelp;
  records: TrainingRecords | undefined;
  entries: OpsTrainingEntry[];
  open: (entry: OpsTrainingEntry) => void;
}

const HelpContext = createContext<OpsHelp | null>(null);

export function useOpsHelp(): OpsHelp {
  const help = useContext(HelpContext);
  if (!help) throw new Error('OpsHelpProvider is missing.');
  return help;
}

export function OpsHelpProvider({ children }: { children: ReactNode }) {
  const { writer } = useServices();
  const walkthroughs = useContext(ContentContext);
  const held = useHeld();
  const records = useMyTrainingRecords();
  const location = useLocation();
  const navigate = useNavigate();

  const walkthrough = useMemo(() => {
    const w = walkthroughForPath(location.pathname, walkthroughs);
    return w && canSeeWalkthrough(w, held) ? w : null;
  }, [location.pathname, walkthroughs, held]);
  const entries = useMemo(() => opsTraining(held, walkthroughs), [held, walkthroughs]);

  const page = usePageHelp({
    walkthrough,
    held,
    records,
    onRecord: (taken) => void sendWalkthroughRecord(writer, taken),
  });

  const tour = new URLSearchParams(location.search).get(TOUR_PARAM);
  const { start } = page;
  useEffect(() => {
    if (!tour) return;
    const rest = new URLSearchParams(location.search);
    rest.delete(TOUR_PARAM);
    const search = rest.toString();
    void navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state as unknown },
    );
    if (walkthrough) start(tour === 'demo' ? 'demo' : 'guided');
  }, [tour, walkthrough, start, navigate, location]);

  const open = useCallback(
    (entry: OpsTrainingEntry) =>
      void navigate('path' in entry.open ? `${entry.open.path}?${TOUR_PARAM}=1` : entry.open.from),
    [navigate],
  );

  const value = useMemo(() => ({ page, records, entries, open }), [page, records, entries, open]);
  return (
    <HelpContext.Provider value={value}>
      {children}
      {page.overlay}
    </HelpContext.Provider>
  );
}

/** The first-visit nudge, above the screen's content. */
export function OpsHelpNudge() {
  return useOpsHelp().page.nudge;
}

/** The Help button of the phone header. */
export function OpsHelpMenu() {
  const { page } = useOpsHelp();
  const navigate = useNavigate();
  const has = page.steps.length > 0;
  return (
    <HelpMenu
      buttonRef={page.buttonRef}
      onGuided={has ? () => page.start('guided') : undefined}
      onDemo={has ? () => page.start('demo') : undefined}
      onTraining={() => void navigate('/training')}
    />
  );
}
